/**
 * In Play page.
 *
 * @module app/pages/InPlayPage
 */
import _ from 'lodash';
import BasePage from './BasePage';
import BodyStateAdapter from '../components/BodyStateAdapter';
import LiveScore from '../components/LiveScore';
import SelectionViewAdapter from '../views/SelectionViewAdapter';
import URLProvider from '../components/URLProvider';
import MessageBusTopics from '../../common/MessageBusTopics';
import Translator from '../components/Translator';
import DeviceDetector from '../components/DeviceDetector';

/**
 * Internal state flag used to
 * determine whether page has been
 * rendered or not
 *
 * @type {Boolean}
 */
var rendered = false;

/**
 * Page content in the form of a jQuery element
 *
 * @type {Object}
 */
var content = null;

/**
 * Page dependencies
 *
 * @type {Object}
 */
var deps = null;

/**
 * Page arguments
 *
 * @type {Object}
 */
var args = null;

/**
 * Moment date object representing time now, gets updated with every setCountDownTimer call
 *
 * @type {Object}
 */
var now = null;

/**
 * Cache for the current list of in play sports will allow us to detect at the moment when a new
 * event is pushed if we should increase the sport counter that is rendered in the banners or add a new
 * entry to the sidebar sport list navigation.
 *
 * @type {Array} where each element has a String sportSlug and an JQuery Object holding the menu entry
 */
var sportNavigationItems = [];

/**
 * Cache for the current list of contextual navigation menu items.
 *
 * @type {Array} where each element has a String sportSlug and an JQuery Object holding the menu entry
 */
var contextualNavigationItems = [];

/**
 * A flag indicating that the page contains a menu with all in play sports
 */
var hasAllSportsNavigation = null;

/**
 * Will hold the sport menu container to avoid finding it each time a new event is push
 */
var sportNavigationContainer = null;

/**
 * Caching the contextual menu, the one displayed on mobiles
 */
var contextualMenu = null;

/**
 * Pagination
 *
 * @type {Object}
 */
var pagination = {

    /**
     * Base link
     *
     * @type {String}
     */
    baseLink: null,

    /**
     * Collection of pointers with first/last sub/group IDs
     *
     * @type {Array}
     */
    pointers: [],

    /**
     * Pointers for first/last group/subgroup
     *
     * @type {Object}
     */
    currentPointers: null,

    /**
     * XMLHttpRequest
     *
     * @type {XMLHttpRequest}
     */
    request: null,

    /**
     * Row IDs
     *
     * @type {Array}
     */
    rowIds: [],

    /**
     * Lazy Loading initialize
     *
     * @returns {void}
     *
     * @private
     */
    init: function init () {

        if (window.WH.sportsbook.totalPages > 1) {

            pagination.initializePagination();

            // init lazy loading
            BasePage.LazyLoadingViewAdapter.init({
                totalPages: window.WH.sportsbook.totalPages,
                container: '#sport-events',
                group: '.comp-container',
                subGroup: '.mg-wrapper, .btmarket__wrapper',
                row: '.event',
                selection: '.market__button, .oddsbutton',
                selectionOdds: '.selectiondetails, .betbutton__odds',
                replaceGroupStr: '<!-- subHeaderPlaceholder -->',
                replaceSubGroupStr: '<!-- eventPlaceholder -->',
                totalItemsPerPage: window.WH.sportsbook.eventsPerPage,
                pagination: pagination,
                highlightedSelections: SelectionViewAdapter.highlightedSelections,
                totalRowsCount: window.WH.sportsbook.totalRowsCount,
            });
        }
    },

    /**
     * Abort Ajax request if needed
     *
     * @returns {void}
     *
     * @private
     */
    abort: function abort () {

        if(pagination.request && typeof pagination.request.abort === 'function') {
            pagination.request.abort();
        }
    },

    /**
     * Gets more data to Lazy Load
     *
     * @param {Number}   rowNumber
     * @param {String}   direction
     * @param {Function} callback
     *
     * @returns {void}
     *
     * @private
     */
    getMoreData: function getMoreData (rowNumber, direction, callback) {

        var pageNumber = Math.ceil(rowNumber / parseInt(window.WH.sportsbook.eventsPerPage, 10));

        if (pageNumber > 0 && pageNumber <= parseInt(window.WH.sportsbook.totalPages, 10)) {
            pagination.getPage(rowNumber, direction, callback);
        } else {
            callback({ totalRows: 0, compiled: [] }, direction, rowNumber);
        }
    },

    /**
     * Get data for selected page
     *
     * @param   {Number}   rowNumber
     * @param   {String}   direction
     * @param   {Function} callback
     *
     * @returns {void}
     *
     * @private
     */
    getPage: function getPage (rowNumber, direction, callback) {

        var pageNumber = Math.ceil(rowNumber / parseInt(window.WH.sportsbook.eventsPerPage, 10));

        if (direction === 1) {

            pagination.request = deps.pageCommon.getJSONResource(
                pagination.baseLink + pageNumber + '.json',
                {
                    'afterSuccess': pagination.getPageSuccess.bind(this, callback, rowNumber),
                    'afterError': callback.bind(this, { totalRows: 0, compiled: [] }, direction),
                }
            );
        }
    },

    /**
     * Function to filter already existing data
     *
     * @param   {Object}   data
     * @param   {Number}   rowNumber
     * @param   {Number}   direction
     *
     * @returns {void}
     *
     * @private
     */
    filterPaginationData: function filterPaginationData (data, rowNumber, direction) {

        var eventsToFilter = 0;

        if (direction === -1) {
            eventsToFilter = (
                (rowNumber-1) % parseInt(window.WH.sportsbook.eventsPerPage, 10)
            ) + 1;

            eventsToFilter = Math.min(
                parseInt(window.WH.sportsbook.eventsPerPage, 10),
                Object.keys(data.data.model.events).length
            ) - eventsToFilter;
        } else {
            eventsToFilter = (
                (rowNumber-1) % parseInt(window.WH.sportsbook.eventsPerPage, 10)
            );
        }

        pagination.removeCompiledRows(
            data,
            eventsToFilter,
            direction
        );
    },

    /**
     * Remove compiled rows that we don't need
     *
     * @param   {Object} data
     * @param   {Array} eventsToFilter
     * @param   {Number} direction
     *
     * @returns {void}
     *
     * @private
     */
    removeCompiledRows: function removeCompiledRows (data, eventsToFilter, direction) {

        /**
         * A set of helper functions
         *
         * @type {Object}
         */
        var helperFunctions = {
            /**
             * Remove groups
             *
             * @param   {Object} group
             *
             * @returns {Boolean}
             *
             * @private
             */
            groupsRemove: function groupsRemove (group) {

                group.rows = _.filter(group.rows, helperFunctions.subgroupsRemove);

                if (group.rows.length === 0) {
                    return false;
                } else {
                    return true;
                }
            },
            /**
             * Remove subgroups
             *
             * @param   {Object} subgroup
             *
             * @returns {Boolean}
             *
             * @private
             */
            subgroupsRemove: function subgroupsRemove (subgroup) {

                subgroup.rows = _.filter(subgroup.rows, helperFunctions.rowsRemove);

                if (subgroup.rows.length === 0) {
                    return false;
                } else {
                    return true;
                }
            },
            /**
             * Remove rows
             *
             * @param   {Object} row
             *
             * @returns {Boolean}
             *
             * @private
             */
            rowsRemove: function rowsRemove (row) {

                return !_.includes(rowsToRemove, row.key);
            },
        };

        if (eventsToFilter > 0) {
            pagination.rowIds = [];
            var rowsToRemove = pagination.rowsToRemove(data, eventsToFilter, direction);
            pagination.specialRows(data, rowsToRemove, direction);
            data.data.compiled = _.filter(data.data.compiled, helperFunctions.groupsRemove);
        }
    },

    /**
     * For given key, find it's group and subgroup
     *
     * @param   {String} key
     * @param   {Object} data
     *
     * @returns {Object}
     *
     * @private
     */
    findParents: function findParents (key, data) {

        var parents = { 'group': null, 'subgroup': null };

        _.find(data.data.compiled, pagination.findGroup.bind(this, key, parents));

        return parents;
    },

    /**
     * Find given group
     *
     * @param   {String} key
     * @param   {Object} parents
     * @param   {Object} group
     *
     * @returns {Boolean}
     *
     * @private
     */
    findGroup: function findGroup (key, parents, group) {

        if (_.find(group.rows, pagination.findSubgroup.bind(this, key, parents))) {
            parents.group = group.key;
            return true;
        }

        return false;
    },

    /**
     * Find if given subgroup is in group
     *
     * @param   {String} key
     * @param   {Object} parents
     * @param   {Object} subgroup
     *
     * @returns {Boolean}
     *
     * @private
     */
    findSubgroup: function findSubgroup (key, parents, subgroup) {

        if (_.find(subgroup.rows, pagination.findRow.bind(this, key))) {
            parents.subgroup = subgroup.key;
            return true;
        }

        return false;
    },

    /**
     * Find if given row is in subgroup
     *
     * @param   {String} key
     * @param   {Object} row
     *
     * @returns {Boolean}
     *
     * @private
     */
    findRow: function findRow (key, row) {

        return row.key === key;
    },

    /**
     * Special rows handling for partial pages
     *
     * @param   {Object} data
     * @param   {Array} rowsToRemove
     * @param   {Number} direction
     *
     * @returns {void}
     *
     * @private
     */
    specialRows: function specialRows (data, rowsToRemove, direction) {

        var eventIds = Object.keys(data.data.model.events);

        if (direction === -1) {
            pagination.removeExistingGroupTemplate(
                pagination.findParents(eventIds[eventIds.length - 1], data),
                pagination.findParents(rowsToRemove[0], data),
                data.data.compiled
            );
        } else {
            pagination.removeExistingGroupTemplate(
                pagination.findParents(eventIds[0], data),
                pagination.findParents(rowsToRemove[rowsToRemove.length - 1], data),
                data.data.compiled
            );
        }
    },

    /**
     * Loop over subgroups
     *
     * @param   {Object} group
     *
     * @returns {void}
     *
     * @private
     */
    groupRows: function groupRows (group) {
        _.each(group.rows, pagination.subgroupRowIds);
    },

    /**
     * Loop over rows
     *
     * @param   {Object} subgroup
     *
     * @returns {void}
     *
     * @private
     */
    subgroupRowIds: function subgroupRowIds (subgroup) {
        pagination.rowIds = pagination.rowIds.concat(_.map(subgroup.rows, 'key'));
    },

    /**
     * Obtain row IDs given the compiled Array
     *
     * @param   {Array} compiled
     *
     * @returns {void}
     *
     * @private
     */
    obtainRowIds: function obtainRowIds (compiled) {
        _.each(compiled, pagination.groupRows);
    },

    /**
     * Select rows to be removed
     *
     * @param   {Object} data
     * @param   {Array} eventsToFilter
     * @param   {Number} direction
     *
     * @returns {Array}
     *
     * @private
     */
    rowsToRemove: function rowsToRemove (data, eventsToFilter, direction) {

        pagination.obtainRowIds(data.data.compiled);

        if (direction === -1) {
            pagination.rowIds = pagination.rowIds.splice(-1 * eventsToFilter);
        } else {
            pagination.rowIds = pagination.rowIds.splice(0, eventsToFilter);
        }

        _.each(pagination.rowIds, function(eventId) {
            delete data.data.model.events[eventId];
        });

        return pagination.rowIds;
    },

    /**
     * Do model updates
     *
     * @param {Object} model
     *
     * @returns {void}
     *
     * @private
     */
    doModelUpdates: function doModelUpdates (model) {
        BasePage.ModelComposer.extendModel(model);
        pagination.updateSubscription(model);
    },

    /**
     * Function called after obtaining next page data to parse it and save it
     *
     * @param   {Function} callback
     * @param   {Number}   rowNumber
     * @param   {Object}   data
     *
     * @returns {void}
     *
     * @private
     */
    saveNextPage: function saveNextPage (callback, rowNumber, data) {

        pagination.filterPaginationData(data, rowNumber, 1);

        pagination.removeExistingGroupTemplate(
            pagination.pointers[pagination.pointers.length-1].last,
            data.data.pointers.first,
            data.data.compiled
        );

        pagination.handlePointers(data.data.pointers);
        pagination.doModelUpdates(data.data.model, true);

        if (typeof callback === 'function') {
            callback({
                totalRows: _.size(data.data.model.events),
                compiled: data.data.compiled,
            }, 1);
        }
    },

    /**
     * Function to handle a success response from fetching a page, but that does not contain any data.
     * This can occur when the server has an unhandled timeout for example.
     *
     * @param callback A function to call with the result of saving: nr of rows and compiled data
     *
     * @returns {void}
     *
     * @private
     */
    handleEmptyPageData: function handleEmptyPageData (callback) {

        if (typeof callback === 'function') {

            callback({
                totalRows: 0,
                compiled: [],
            }, 1);
        }
    },

    /**
     * Function called after a new page was successfully fetched. It handles the scenario where we have a
     * success response but might be empty, due to timeouts on the server for example.
     *
     * @param {Function} callback A function to call with the result of saving: nr of rows and compiled data
     * @param {integer} rowNumber The row number this page starts from
     * @param {Object} data Fetched from the server representing the page (model, pointers, compiled ...)
     *
     * @returns {void}
     *
     * @private
     */
    getPageSuccess: function getPageSuccess (callback, rowNumber, data) {

        if (!_.isEmpty(data) && !_.isEmpty(data.data)) {

            pagination.saveNextPage(callback, rowNumber, data);
        } else {

            pagination.handleEmptyPageData(callback);
        }
    },

    /**
     * Removes existing subgroup template if present on current page
     *
     * @param   {Object} currentPointers
     * @param   {Object} newPointers
     * @param   {Object} data
     *
     * @returns {void}
     *
     * @private
     */
    removeExistingSubgroupTemplate: function removeExistingSubgroupTemplate(currentPointers, newPointers, data) {

        var group = _.find(data, { 'key': newPointers.group });
        const pointersMatch = currentPointers.subgroup === newPointers.subgroup;

        if (pointersMatch && _.find(group.rows, { 'key': newPointers.subgroup })) {
            delete _.find(group.rows, { 'key': newPointers.subgroup }).template;
        }
    },

    /**
     * Removes existing group template if present on current page
     *
     * @param   {Object} currentPointers
     * @param   {Object} newPointers
     * @param   {Object} data
     *
     * @returns {void}
     *
     * @private
     */
    removeExistingGroupTemplate: function removeExistingGroupTemplate (currentPointers, newPointers, data) {

        if (currentPointers.group === newPointers.group &&
            BasePage.jQuery('[data-entityid="' + newPointers.group + '"]').length &&
            _.find(data, { 'key': newPointers.group })) {
            delete _.find(data, { 'key': newPointers.group }).template;
            pagination.removeExistingSubgroupTemplate(currentPointers, newPointers, data);
        }
    },

    /**
     * Initializes pagination
     *
     * @returns {void}
     *
     * @private
     */
    initializePagination: function initializePagination () {

        pagination.request = null;
        pagination.pointers = [];
        pagination.baseLink = window.WH.sportsbook.baseLink;
        pagination.handlePointers(window.WH.sportsbook.currentPointers);
    },

    /**
     * This functions handles removing events from model
     *
     * @param   {Array} eventsToRemove
     *
     * @returns {void}
     *
     * @private
     */
    updateModel: function updateModel (eventsToRemove) {

        BasePage.ModelComposer.removeEventsFromModel(eventsToRemove);
    },

    /**
     * Subscribed to given events and their descendants
     *
     * @param {Object} model
     *
     * @returns {void}
     *
     * @private
     */
    updateSubscription: function updateSubscription (model) {
        _.each(
            model,
            subscribeSetToPush
        );
    },

    /**
     * Add pointers
     *
     * @param {Object} newPointers
     *
     * @returns {void}
     *
     * @private
     */
    handlePointers: function handlePointers (newPointers) {

        if(pagination.pointers.length >= 5) {
            pagination.pointers = pagination.pointers.slice(1, 5);
        }

        pagination.pointers.push(newPointers);
    },

    /**
     * After lazy load we will handle the start time for the newly lazy loaded events.
     *
     * @param {Array} batches DocumentFragments containing events
     *
     * @returns {void}
     *
     * @private
     */
    postLoad: function postLoad (batches) {

        /**
         * Finds in the model the respective event based on the node id and handles start time
         *
         * @param {Object} node A node representing an event containing the Id to search the model.
         *
         * @returns {void}
         *
         * @private
         */
        var processEvent = function processEvent (node) {

            var event = deps.sportsDataRepository.getModel().events[node.id];

            LiveScore.subscribe(event, content);
        };

        /**
         * Extracts from a batch all the events and calls for individual processing of each of them.
         *
         * @param {DocumentFragment} batch Events grouped under a DocumentFragment
         *
         * @returns {void}
         *
         * @private
         */
        var processBatch = function processBatch (batch) {

            _.each(batch.querySelectorAll('.event'), processEvent);
        };

        _.each(batches, processBatch);
    },
};

/**
 * Function used to initialize views on the page
 *
 * @returns {void}
 *
 * @private
 */
var initViews = function initViews () {

    var model = deps.sportsDataRepository.getModel();

    SelectionViewAdapter.init(model.selections, content);

    // Subscribe live events to live score updates
    _.each(model.events, (anEvent) => LiveScore.subscribe(anEvent, content));

    setTimeout(
        LiveScore.setCountDownTimer,
        getMiliSecondsLeftToNextMinute()
    );
};

/**
 * Function used to initialize the model
 *
 * @params {Boolean} regenerateModel Flag to indicate if the model should be regenerated
 *
 * @returns {void}
 *
 * @private
 */
var initModel = function initModel (regenerateModel) {

    BasePage.setOddsFormat();

    if (regenerateModel === true) {
        BasePage.ModelComposer.regenerateMergedModel();
    }

    if (deps && deps.pageCommon) {
        deps.pageCommon.PushAdapter.unsubscribeAll();

        _.each(
            deps.sportsDataRepository.getModel(),
            subscribeSetToPush
        );
    }
};

/**
 * Function used to bind event handles to various buttons/links on the page
 *
 * @param {Object} content jQuery object containing the page content
 * @param {Object} deps Page dependencies
 * @param {Object} args arguments like sport or locale, etc...
 *
 * @returns {void}
 *
 * @private
 */
var bindNavigation = function bindNavigation (pageContent, pageDeps, pageArgs) {

    content = pageContent;
    deps = pageDeps;
    args = pageArgs;

    initSportFilters(content);
};

/**
 * Performs initializations
 *
 * @returns {void}
 *
 * @private
 */
var performInit = function performInit () {

    initViews();
    initModel(false);
};

/**
 * Performs postRender initializations
 *
 * @returns {void}
 *
 * @private
 */
var initPostRender = function initPostRender () {

    performInit();
    setAllSportsNavigation();

    BasePage.jQuery('.main').find('#contextual-navigation a:not(.non-route)')
        .off('click.SidebarNavigation');
};

/**
 * The list of sports navigation items as elements because we want to use them to insert before/after.
 * Keeping the first 'all sports' entry for the same reason, so we always have something 'before'.
 *
 * @returns {Array} jQuery elements
 *
 * @private
 */
var cacheSportNavigationItems = function cacheSportNavigationItems () {

    /**
     * Given a navigation item it's extracting the sport slug from the attributes of the element.
     *
     * @param {Object} navItem The event data
     *
     * @returns {Object} the slug of the sport
     *
     * @private
     */
    var extractSportSlug = function extractSportSlug (navItem) {

        var sportNavItem = BasePage.jQuery(navItem);

        return {
            sportSlug: sportNavItem.children().attr('data-sport-slug'),
            element: sportNavItem,
        };
    };

    //cache left sidebar navigation
    sportNavigationContainer = BasePage.jQuery('#market-collections');

    var sportNavigationItems = sportNavigationContainer.find('.marketmenu__section');
    sportNavigationItems = _.map(sportNavigationItems, extractSportSlug);

    //cache contextual mobile navigation
    contextualMenu = BasePage.jQuery('#contextual-navigation-menu');
    contextualNavigationItems = _.map(contextualMenu.children(), extractSportSlug);
};

/**
 * Gets the number of miliseconds between the current time and the next minute
 *
 * @return {integer}
 *
 * @private
 */
var getMiliSecondsLeftToNextMinute = function getMiliSecondsLeftToNextMinute () {
    return (60 - now.get('seconds')) * 1000;
};

/**
 * Subscribe a set of entities (events, markets, etc) to PUSH updates
 *
 * @param {Object} set An object containing entities such as markets, selections, events
 *
 * @returns {void}
 *
 * @private
 */
var subscribeSetToPush = function subscribeSetToPush (set) {
    _.each(set, subscribeEntityToPush);
};

/**
 * Subscribe a single entity to PUSH updates
 *
 * @param {Object} entity Market/event/selection model
 *
 * @returns {void}
 *
 * @private
 */
var subscribeEntityToPush = function subscribeEntityToPush (entity) {

    if (entity && entity.id && !entity.id.match(/OB_TY/g) &&
        deps.pageCommon.DomPushAdapter.subscribe(entity, push.callback)) {

        deps.pageCommon.PushAdapter.subscribe(entity.pdsTopic);
    }
};

/**
 * Gets the request and redirect url for all sports
 *
 * @returns {Object}
 *
 * @private
 */
var getAllSportsURLSet = function getAllSportsURLSet () {

    var requestURL = URLProvider.inPlay.allSportsFragment(args);
    var redirectURL = URLProvider.inPlay.allSportsFull(args);

    return {
        requestURL: requestURL,
        redirectURL: redirectURL,
    };
};

/**
 * Gets the request and redirect url for a single sport
 *
 * @returns {Object}
 *
 * @private
 */
var getSingleSportURLSet = function getSingleSportURLSet () {

    var requestURL = URLProvider.inPlay.singleSportFragment(args);
    var redirectURL = URLProvider.inPlay.singleSportFull(args);

    return {
        requestURL: requestURL,
        redirectURL: redirectURL,
    };
};

/**
 * Function to set the request and redirect URL based on the current sport
 *
 * @returns {Object}
 *
 * @private
 */
var getURLSet = function getURLSet () {

    if (args.sport === 'in-play') {

        return getAllSportsURLSet();
    } else {

        return getSingleSportURLSet();
    }
};

/**
 * Will restore page functionality after a fragment load in terms of view: sidebar, other navigation ...
 *
 * @returns {void}
 *
 * @private
 */
var reinitializeViews = function reinitializeViews () {

    initViews();
};

/**
 * Function to call after we fetched a new fragment in order to add back functionality to the page.
 *
 * @returns {void}
 *
 * @private
 */
var reloadSportSuccess = function reloadSportSuccess () {

    initModel(true);
    reinitializeViews();

    BasePage.UrlArgsProvider.update();

    // pagination
    BasePage.LazyLoadingViewAdapter.destroy();
    pagination.init();
    window.WH.messageBus.publish(MessageBusTopics.NAVIGATION_PAGE_CHANGED, { skipTopBetsReload: true });
};

/**
 * Clears the active menu in the left sidebar sport list
 *
 * @returns {void}
 *
 * @private
 */
var clearSportNavigationActiveMenuItem = function clearSportNavigationActiveMenuItem () {

    if (sportNavigationContainer) {

        sportNavigationContainer.find('a.sportmenu__link.-active').removeClass('-active');
    }
};

/**
 * Clears the active menu in the contextual menu
 *
 * @returns {void}
 *
 * @private
 */
var clearContextualActiveMenuItem = function clearContextualActiveMenuItem () {

    if (contextualMenu) {

        contextualMenu.find('.contextual-nav__item.contextual-nav__item--active').
            removeClass('contextual-nav__item--active');
    }
};

/**
 * Clear both selected menu entries from desktop and contextual
 *
 * @returns {void}
 *
 * @private
 */
var clearActiveMenuItems = function clearActiveMenuItems () {

    clearSportNavigationActiveMenuItem();
    clearContextualActiveMenuItem();
};

/**
 * Set the active menu in the left sidebar sport list
 *
 * @returns {void}
 *
 * @private
 */
var setSportNavigationActiveMenuItem = function setSportNavigationActiveMenuItem () {

    if (sportNavigationContainer) {

        sportNavigationContainer.find('a[data-sport-slug=\'' + args.sport + '\']').
            addClass('-active');
    }
};

/**
 * Set the active menu in the contextual menu
 *
 * @returns {void}
 *
 * @private
 */
var setContextualActiveMenuItem = function setContextualActiveMenuItem () {

    if (contextualMenu) {

        contextualMenu.find('a[data-sport-slug=\'' + args.sport + '\']').parent().
            addClass('contextual-nav__item--active');
    }
};

/**
 * Set active menu entries from desktop and contextual
 *
 * @param {Object} sportFilter The jquery object
 *
 * @returns {void}
 *
 * @private
 */
var setActiveMenuItems = function setActiveMenuItems (sportFilter) {

    //if the filtering was triggered on the sidebar left sports menu
    if (sportFilter.hasClass('sportmenu__link')) {

        sportFilter.addClass('-active');
        setContextualActiveMenuItem();
    } else {

        //if the filtering was triggered on the contextual menu
        sportFilter.parent().addClass('contextual-nav__item--active');
        setSportNavigationActiveMenuItem();
    }
};

/**
 * Function to reload page fragment
 *
 * @param {Object} sportFilter The jquery object
 *
 * @returns {void}
 *
 * @private
 */
var onSportFilterChange = function onSportFilterChange(sportFilter) {

    if (!sportFilter.hasClass('-active') &&
        !sportFilter.parent().hasClass('contextual-nav__item--active')) {

        clearActiveMenuItems();
        setActiveMenuItems(sportFilter);

        reloadSportFragment();
    }
};

var reloadSportFragment = function reloadSportFragment() {

    args.pagination = 0;

    var URLSet = getURLSet();
    deps.pageCommon.reloadSnippet(URLSet, '#sport-events', {
        'afterSuccess': reloadSportSuccess,
    });
};

/**
 * Action handling click on a sport filter
 *
 * @returns {Boolean}
 *
 * @private
 */
var sportFilterAction = function sportFilterAction() {

    var sportFilter = BasePage.jQuery(this);
    var streamingFilterDisabled = !sportFilter.data('has-streamed-events');
    var streamingFilter = BasePage.jQuery('#streaming-events-toggle')[0];
    var streamingFilterSlider = BasePage.jQuery('#streaming-events-filter__slider');
    streamingFilter.disabled = streamingFilterDisabled;

    if (streamingFilterDisabled) {
        streamingFilterSlider.addClass('streaming-events-filter__slider--disabled');
    } else {
        streamingFilterSlider.removeClass('streaming-events-filter__slider--disabled');
    }
    args.sport = sportFilter.data('sport-slug');
    onSportFilterChange(sportFilter);

    if (isHiddenLive()) {
        hasHtmlChanged.prevHtml = document.getElementsByClassName('column__primary')[0].innerHTML || '';
        initializeMutationObserver();
    }
    return false;
};

/**
 * Action handling click on a streaming filter
 *
 * @private
 */
var streamingFilterAction = function streamingFilterAction() {

    push.clearRemovedEvents(); // the reason is the contextual navigation reloads as well
    var streamingFilter = BasePage.jQuery(this);
    var streamingFilterPath = !args.streamed ? '/streamed' : '';
    var sport = args.sport === 'in-play' ? 'all' : args.sport;
    streamingFilter.checked = !streamingFilter.checked;
    BasePage.PageCommon.navigate('/betting/' + args.locale + '/in-play/' + sport + streamingFilterPath);
};


const sortTranslations = {
    'es-es': {
        'Competitions': 'Competición',
        'Streaming': 'Streaming',
    },
    'it-it': {
        'Competitions': 'Competizione',
        'Streaming': 'Streaming',
    },
};

const sortOptions = [
    'Competitions',
    'Streaming',
];

const availableSportsForSelects = ['football'];

/**
 * render selects options for modals
 *
 * @private
 */
const renderSelectOptions = (listId, list, modalId, activeFilterId) => {
    const listElement = document.getElementById(listId);
    listElement.innerHTML = '';

    list.forEach(function (item) {
        const translatedItem = sortTranslations[args.locale][item] || item;
        const itemDiv = document.createElement('div');
        itemDiv.classList.add('select-item');
        itemDiv.addEventListener('click', (event) => marketFilterAction(event.currentTarget));

        const svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        svgElement.setAttribute('fill', 'rgb(0, 20, 60)');
        svgElement.setAttribute('viewBox', '0 0 24 24');
        svgElement.setAttribute('stroke', 'rgb(0, 20, 60)');
        svgElement.setAttribute('width', '12');
        svgElement.setAttribute('height', '12');
    
        const pathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        pathElement.setAttribute('d', 'M23.1249' +
            '4.25647L22.7426 3.87418C22.4451 3.57761 22.0834 3.42847 21.6591 3.42847' +
            'C21.2349 3.42847 20.874 3.57761 20.5766 3.87418L8.57143 15.8793L2.99571 10.3028' +
            'C2.69743 10.0062 2.33657 9.85704 1.91229 9.85704C1.48714 9.85704 1.12543 10.0062 0.828' +
            ' 10.3036L0.445714 10.6859C0.149143 10.9842 0 11.345 0 11.7693C0 12.1936 0.149143' +
            ' 12.5545 0.445714 12.8519L6.87514 19.7433L7.25743 20.1256C7.55571 20.4222 7.91657' +
            ' 20.5713 8.34086 20.5713C8.76514 20.5713 9.126 20.4222 9.42343 20.1256L23.1257' +
            ' 6.42418C23.4223 6.1259 23.5714 5.7659 23.5714 5.34075C23.5714 4.91561 23.4223' +
            ' 4.55475 23.1249 4.25647Z');
        svgElement.classList.add('hidden');

        const textElement = document.createElement('div');
        textElement.textContent = translatedItem;
        itemDiv.appendChild(textElement);

        svgElement.appendChild(pathElement);
        itemDiv.appendChild(svgElement);

        itemDiv.setAttribute('data-activeFilter', activeFilterId);
        itemDiv.setAttribute('data-modal', modalId);
        itemDiv.setAttribute('data-value', item);
        listElement.appendChild(itemDiv);
    });
};

/**
 * Changes visibility of modal with select options
 *
 * @private
 */
const handleMarketModalStatus = (target) => {
    const modalId = target.getAttribute('data-modal');
    const marketTypesSelect = document.getElementById(modalId);
    const mainContainer = document.getElementById('main-container');
    mainContainer.style.zIndex = mainContainer.style.zIndex === '10' ? 'unset' : '10';
    marketTypesSelect.style.visibility = marketTypesSelect.style.visibility === 'visible' ? 'hidden' : 'visible';
};

/**
 * Checks if select should render on active locale
 *
 * @private
 */
const isLocaleAllowed = () => {
    const allowedJurs = ['es-es', 'it-it'];
    return allowedJurs.includes(args.locale);
};

/**
 * Checks if sort is available on the page
 *
 * @private
 */
const isStreamingAvailable = () => {
    const streamingElements = document.querySelectorAll('[data-streaming]');

    for (const element of streamingElements) {
        if (element.getAttribute('data-streaming') === 'true') {
            return true;
        }
    }

    return false;
};

/**
 * Toggle button disable
 *
 * @private
 */
const disableButtonBasedOnAvailability = (buttonSelector, availabilityCheckFunction) => {
    const buttonElement = document.querySelector(buttonSelector);

    if (buttonElement) {
        const isAvailable = availabilityCheckFunction();
        buttonElement.disabled = !isAvailable;
    }
};

/**
 * Checks if there is more than one markets available on the page
 *
 * @private
 */

const isMoreThanOneMarket = async () => {
    const model = deps.sportsDataRepository.getModel();
    const { markets } = model;
    const names = new Set();
    for (const key in markets) {
        if (markets.hasOwnProperty(key)) {
            const market = markets[key];
            if (names.has(market.name)) {
                return true;
            }
            names.add(market.name);
        }
    }
    return false;  
};

/**
 * Adding styles to active market and changes active market in select
 *
 * @private
 */

const updateActiveMarket = (selectedMarket) => {
    const modalId = selectedMarket.getAttribute('data-modal');
    const modalElement = document.getElementById(modalId);
    const typeListElement = modalElement.querySelector('.select-type-list');
    const selectItems = typeListElement.querySelectorAll('.select-item');

    for (let i = 0; i < selectItems.length; i++) {
        const svgElement = selectItems[i].querySelector('svg');
        if (svgElement) {
            svgElement.classList.add('hidden');
        }

        selectItems[i].style.backgroundColor = 'white';
    }

    const selectedSvgElement = selectedMarket.querySelector('svg');
    selectedSvgElement.classList.remove('hidden');

    selectedMarket.style.backgroundColor = 'rgb(235, 241, 255)';
    const firstMarketText = document.getElementById(selectedMarket.getAttribute('data-activeFilter'));
    firstMarketText.textContent = selectedMarket.querySelector('div').textContent;
};

/**
 * Sort markets groups by argument
 *
 * @private
 */
const sortContainers = (argument) => {
    const eventsContainer = document.getElementsByClassName('event-container')[0];
    const marketsGroupsContainer = document.getElementsByClassName('markets-group-container');

    const marketGroupsArray = Array.from(marketsGroupsContainer);
    marketGroupsArray.forEach(marketGroup => {
        const events = marketGroup.querySelectorAll('.event');

        events.forEach(event => {
            const streamingValue = event.getAttribute('data-streaming') === 'true';
            if ((argument === 'Streaming' && streamingValue) || (argument === 'Competitions' && !streamingValue)) {
                eventsContainer.removeChild(marketGroup);
                eventsContainer.prepend(marketGroup);
            }
        });
    });
};

/**
 * Collect all unique markets names from events-group-container
 *
 * @private
 */
const getAllMarketNamesFromHTML = () => {
    const marketsContainers = document.getElementsByClassName('events-group-container');
    const uniqueMarketNames = [];

    for (const market of marketsContainers) {
        const marketName = market.getAttribute('data-market-group-name');

        if (marketName && !uniqueMarketNames.includes(marketName)) {
            uniqueMarketNames.push(marketName);
        }
    }

    return uniqueMarketNames;
};

/**
 * returns if sport is available
 *
 * @private
 */
const isSportAllowed = (sport) => {
    return availableSportsForSelects.includes(sport);
};

/**
 * toggle class hidden for markets based on select
 *
 * @private
 */
const filterByMarket = (selectedMarket) => {
    const marketsContainers = document.getElementsByClassName('events-group-container');

    for (let i = 0; i < marketsContainers.length; i++) {
        const market = marketsContainers[i];
        const marketsContainer = market.closest('.markets-group-container');

        if (marketsContainer) {
            const marketList = marketsContainer.querySelectorAll('.events-group-container');

            marketList.forEach((singleMarket) => {
                const singleMarketName = singleMarket.getAttribute('data-market-group-name');

                if (singleMarketName === selectedMarket) {
                    singleMarket.classList.remove('hidden');
                } else {
                    singleMarket.classList.add('hidden');
                }
            });

            if (marketList.length > 0) {
                marketsContainer.classList.toggle('hidden', !Array.from(marketList).some(singleMarket => {
                    return singleMarket.getAttribute('data-market-group-name') === selectedMarket;
                }));
            }
        }
    }
};

/**
 * Action handling click on a market types filter
 *
 * @private
 */

const marketFilterAction = function marketFilterAction(selectedMarket) {
    const activeFilter = selectedMarket.getAttribute('data-activeFilter');

    if (activeFilter === 'activeMarket') {
        filterByMarket(selectedMarket.getAttribute('data-value'));
    }  else if (activeFilter === 'activeSort') {
        sortContainers(selectedMarket.getAttribute('data-value'));
    }

    updateActiveMarket(selectedMarket);
    handleMarketModalStatus(selectedMarket);
};

/**
 * render selects on the page
 *
 * @private
 */
const initSelectFilters = () => {
    const sport = getSportName();
    if (isLocaleAllowed() &&  areSelectsAllowed() && isSportAllowed(sport)) {

        checkIfDisableSelects(sport);
        
        const mainContainer = document.getElementById('main-container');
        if (mainContainer) {
            mainContainer.style.zIndex = '10';
        }

        //only for hidden live
        const selectsContainer = document.getElementsByClassName('select-container')[0];
        if (selectsContainer) {
            selectsContainer.style.display = 'flex';
        }

        const marketsData = getAllMarketNamesFromHTML();
        renderSelectOptions('market-type-list', marketsData, 'market-modal', 'activeMarket');
        filterByMarket(marketsData[0]);
        const defaultMarketName = `[data-value="${marketsData[0]}"]`;
        const defaultMarketElement = document.querySelector(defaultMarketName);
        updateActiveMarket(defaultMarketElement);
    
        renderSelectOptions('sort-type-list', sortOptions, 'sort-modal', 'activeSort');
        sortContainers('Competitions');
        const defaultSortName = '[data-value="Competitions"]';
        const defaultSortElement = document.querySelector(defaultSortName);
        updateActiveMarket(defaultSortElement);

        disableButtonBasedOnAvailability('button[data-modal="sort-modal"]', isStreamingAvailable);
        disableButtonBasedOnAvailability('button[data-modal="market-modal"]', isMoreThanOneMarket);

    } else {
        const selectsContainer = document.getElementsByClassName('select-container')[0];
        if (selectsContainer) {
            selectsContainer.style.display = 'none';
        }
    }
};

/**
 * returns if selects are allowed on device
 *
 * @private
 */
const areSelectsAllowed = () => {
    return (DeviceDetector.isMobile() || DeviceDetector.isNative());
};

/**
 * returns if hiddenLive is active
 *
 * @private
 */
const isHiddenLive = () => {
    return window && window.localStorage.getItem('inplay');
};

/**
 * Toggle selects disable status
 *
 * @private
 */
const checkIfDisableSelects = (sport) => {
    const selectsContainer = document.querySelector('.select-container');
    selectsContainer.style.display = isSportAllowed(sport) ? 'flex' : 'none';
};

/**
 * return english sport name
 *
 * @private
 */
const getSportName = () => {
    if (window.WH && window.WH.sportsbook && window.WH.sportsbook.sportEn) {
        return window.WH.sportsbook.sportEn.toString();
    } else {
        return window.WH.i18n.urlDict.localToEng[args.sport];
    }
};

/**
 * checks if html changed for observer
 *
 * @private
 */
const hasHtmlChanged = () => {
    const sportEvents = document.getElementsByClassName('column__primary')[0];

    if (sportEvents) {
        const currentHtml = sportEvents.innerHTML;

        if (currentHtml !== hasHtmlChanged.prevHtml) {
            hasHtmlChanged.prevHtml = currentHtml;
            return true;
        }
    }

    return false;
};

let observer;

/**
 * Observer for rendering selects after list of events changes
 *
 * @private
 */
const initializeMutationObserver = () => {
    const sportEvents = document.getElementsByClassName('column__primary')[0];

    if (!sportEvents) {
        return;
    }

    observer = new MutationObserver((mutations) => {
        if (hasHtmlChanged()) {
            initSelectFilters();
            observer.disconnect();
        }
    });

    const observerConfig = { childList: true, subtree: true };

    observer.observe(sportEvents, observerConfig);

};

/**
 * Init sport filters menu for in play page
 *
 * @param {Object} content jQuery element
 *
 * @returns {void}
 *
 * @private
 */
var initSportFilters = function initSportFilters (content) {

    var bindings = [{
        selector: '#market-collections',
        filter: 'a.sportmenu__link',
        events: [{
            event: 'click.sportFilter',
            functions: [sportFilterAction],
        }],
    }, {
        selector: '#contextual-navigation',
        filter: 'a.btn',
        events: [{
            event: 'click.contextualSportFilter',
            functions: [sportFilterAction],
        }],
    }, {
        selector: '#streaming-events-toggle',
        events: [{
            event: 'click.contextualStreamingFilter',
            functions: [streamingFilterAction],
        }],
    },
    {
        selector: '.select-button',
        events: [{
            event: 'click',
            functions: [(event) => handleMarketModalStatus(event.currentTarget)],
        }],
    },
    {
        selector: '.exit-select-modal',
        events: [{
            event: 'click',
            functions: [(event) => handleMarketModalStatus(event.currentTarget)],
        }],
    }];

    if (isHiddenLive()) {
        initSelectFilters();
    }

    _.each(bindings, bindEventsActions.bind(this, content));
};

/**
 * Binds given functions to a jQuery selector 'on' events
 *
 * @param {Object} content jQuery element
 * @param {Object} binding Object with required attributes:
 *      {
 *         selector: '#selector',
 *         events: [{
 *            event: 'click.sportFilter',
 *            functions: [sportFilterAction, hideLeftOffCanvas]
 *         }]
 *      }
 *
 * @returns {void}
 *
 * @private
 */
var bindEventsActions = function bindEventsActions (content, binding) {

    var selector = binding.selector;

    /**
     * Applies event to jQuery 'on' method, but before it unbinds previous event using 'off' method
     *
     * @param {Object} eventData Properties: 'ns' event namespace, 'functions' Array of functions to bind
     *
     * @returns {void}
     *
     * @private
     */
    var applyEvent = function applyEvent (eventData) {

        var event = eventData.event;

        /**
         * Binds a function to the given selector on a given event
         *
         * @param {Function} bindFunction jQuery element
         *
         * @returns {void}
         *
         * @private
         */
        var applyBindFunction = function applyBindFunction (bindFunction) {

            content.find(selector).on(event, binding.filter, bindFunction);
        };

        content.find(selector).off(event);
        _.each(eventData.functions, applyBindFunction);
    };

    _.each(binding.events, applyEvent);
};

/**
 * This function checks if the event was the only one left in his container in order to remove empty
 * parents.
 *
 * @param {String} eventId the id of the event
 * @param {Object} $event jQuery element
 * @param {Boolean} hide Hide flag
 *
 * @returns {void}
 *
 * @private
 */
var manageEventContainersHeaders = function manageEventContainersHeaders (eventId, $event, hide) {

    if (hide) {

        //model composer will remove the event with the markets and selections
        BasePage.ModelComposer.removeEventsFromModel([eventId]);

        checkEmptyParents($event);
    }
};

/**
 * Check for empty parents
 *
 * @param {Object} $event jQuery element
 *
 * @returns {void}
 *
 * @private
 */
var checkEmptyParents = function checkEmptyParents ($event) {

    if ($event.siblings('.event').length === 0) {

        //market group is now empty, since the event has no siblings, let's further check the competition
        var marketGroup = $event.parents('.events-group-container');
        removeEmptyMarketGroup(marketGroup);
    } else {

        //we completely remove the event so in the future we can check for the number of siblings...
        $event.remove();
    }
};

/**
 * Will remove an empty market group and further check if the whole competition must be removed if left
 * empty. To check if we have to propagate remove to the parent competition, the safe way is to
 * first get the parent competition then find markets in the descendants. We can not take a decision
 * based only of the number of siblings of a market group, since pagination will NOT place the lazy loaded
 * market group at the same level with the first market group rendered.
 *
 * @param {Object} emptyMarketGroup jQuery element
 *
 * @returns {void}
 *
 * @private
 */
var removeEmptyMarketGroup = function removeEmptyMarketGroup (emptyMarketGroup) {

    var competition = emptyMarketGroup.parents('.markets-group-container');

    if (competition.find('.events-group-container').length === 1) {

        var emptyCompetition = emptyMarketGroup.parents('.markets-group-container');
        removeEmptyCompetition(emptyCompetition);
    } else {

        //there are market groups left in the competition so we just delete the current empty one and stop
        emptyMarketGroup.remove();
    }
};

/**
 * Will remove the empty competition and the sport as well if the competition was the last from the sport
 *
 * @param {Object} emptyCompetition jQuery competition container
 *
 * @returns {void}
 *
 * @private
 */
var removeEmptyCompetition = function removeEmptyCompetition (emptyCompetition) {

    var sportContainer = getSportForCompetition(emptyCompetition);
    var childrenCompetitions  = sportContainer.find('.markets-group-container');

    //if the sports has less then 2 children competition we might have to remove it, but we can not tell
    //for sure until we look at the siblings, because pagination is adding competitions as siblings...
    if (childrenCompetitions.length <= 1) {

        removeSportContainer(sportContainer, childrenCompetitions);
    }

    emptyCompetition.remove();
};

/**
 * Will remove a sport if it's empty
 *
 * @param {Object} sportContainer jQuery sport container
 * @param {Object} childrenCompetitions the children competition of the sport
 *
 * @returns {void}
 *
 * @private
 */
var removeSportContainer = function removeSportContainer (sportContainer, childrenCompetitions) {

    var siblingsCompetitions =
        sportContainer.nextUntil('.sport-events-container', '.markets-group-container');
    //if the empty competition was the last one in the sport ...
    if (childrenCompetitions.length + siblingsCompetitions.length === 1) {

        updateSport(sportContainer.attr('data-sport-slug'));
        sportContainer.remove();
    }
};

/**
 * the sport of the competition to remove might be the parent of the competition or the previous sibling ,
 * due to how pagination works for this page.
 *
 * @param {Object} competitionToRemove
 *
 * @returns {Object} sport container
 *
 * @private
 */
var getSportForCompetition = function getSportForCompetition (competitionToRemove) {

    var sportContainer = competitionToRemove.parents('.sport-events-container');

    if (sportContainer.length === 1) {

        return sportContainer;
    } else {

        return competitionToRemove.prev('.sport-events-container');
    }
};

/**
 * If the whole sport-container is hidden - it removes sport menu item and updates sports summary count
 *
 * @param {String} sportSlug sport slug string value
 *
 * @returns {void}
 *
 * @private
 */
var updateSport = function updateSport (sportSlug) {

    if (hasAllSportsNavigation) {

        removeSportFromMenu(sportSlug);
    }
};

/**
 * Get the navigation item from slug
 *
 * @param {String} sportSlug sport slug string value
 *
 * @returns {Object}
 *
 * @private
 */
var getNavItemBySlug = function getNavItemBySlug (sportSlug) {

    var cachedNavItem = _.find(sportNavigationItems, {
        sportSlug: sportSlug,
    });

    if (cachedNavItem) {

        return cachedNavItem.element;
    } else {

        return BasePage.jQuery('#market-collections').find('a[data-sport-slug="' + sportSlug + '"]').
            parent('section.marketmenu__section');
    }
};

/**
 * Get the contextual navigation item from slug
 *
 * @param {String} sportSlug sport slug string value
 *
 * @returns {Object}
 *
 * @private
 */
var getContextualNavItemBySlug = function getContextualNavItemBySlug (sportSlug) {

    var cachedNavItem = _.find(contextualNavigationItems, {
        sportSlug: sportSlug,
    });

    if (cachedNavItem) {

        return cachedNavItem.element;
    } else {

        return BasePage.jQuery('#contextual-navigation-menu').
            find('a[data-sport-slug=\'' + sportSlug + '\']').parent();
    }
};

/**
 * Getting both menu items for a sport: left sports menu and contextual
 *
 * @param {String} sportSlug sport slug string value
 *
 * @returns {Array} the navigation items
 *
 * @private
 */
var getNavItemsFromSlug = function getNavItemsFromSlug (sportSlug) {

    return [
        getNavItemBySlug(sportSlug),
        getContextualNavItemBySlug(sportSlug),
    ];
};

/**
 * Removes from the cache the item with the respective slug
 *
 * @param {String} sportSlug sport slug string value
 *
 * @returns {void}
 *
 * @private
 */
var removeCachedNavItem = function removeCachedNavItem (sportSlug) {

    /**
     * compares the slug of the nav item with the passed one
     *
     * @param {Object} navItem a navigation item from the cache
     *
     * @returns {Boolean} are the slugs identical
     *
     * @private
     */
    var navItemHasSlug = function navItemHasSlug (navItem) {

        return navItem.sportSlug === sportSlug;
    };

    sportNavigationItems =
        _.reject(sportNavigationItems, navItemHasSlug);

    contextualNavigationItems =
        _.reject(contextualNavigationItems, navItemHasSlug);
};

/**
 * Toggles displayNone class for the sport in the sports menu
 *
 * @param {String} sportSlug sport slug string value
 *
 * @returns {void}
 *
 * @private
 */
var removeSportFromMenu = function removeSportFromMenu (sportSlug) {

    /**
     * Removes a menu item
     *
     * @param {Object} navItem A navigation item, either left menu element or contextual element
     *
     * @returns {void}
     *
     * @private
     */
    var removeNavItem = function removeNavItem (navItem) {

        navItem.remove();
        removeCachedNavItem(sportSlug);
    };

    var navItems = getNavItemsFromSlug(sportSlug);
    _.each(navItems, removeNavItem);
};

/**
 * Checks if we need to update events count after displayed push message
 *
 * @param {Object} $event jQuery element
 * @param {Boolean} hide flag if event is being hidden
 *
 * @returns {void}
 *
 * @private
 */
var handleEventsCount = function handleEventsCount ($event, hide) {

    if (hide) {

        var sportSlug = $event.parents('.sport-events-container').attr('data-sport-slug');
        updateSportsMenuEventsCount(sportSlug, -1);
    }
};

/**
 * Updates count for the respective element according to the passed difference
 *
 * @param {Object} countItem The object be updated
 * @param {integer} difference Integer value of events difference from current value
 *
 * @returns {void}
 *
 * @private
 */
var updateCountItem = function updateCountItem (countItem, difference) {

    var newCount = parseInt(countItem.html(), 10) + parseInt(difference, 10);
    //it should not happen to go under 0, but to make sure we don't show negatives in production ...
    if (newCount > -1) {

        countItem.html(newCount);
    }
};

/**
 * Updates Left sports menu items events count according to the passed difference
 *
 * @param {String} sportSlug sport slug text
 * @param {integer} difference Integer value of events difference from current value
 *
 * @returns {void}
 *
 * @private
 */
var updateSportsMenuEventsCount = function updateSportsMenuEventsCount (sportSlug, difference) {

    /**
     * Updates sports menu item events count according to the passed
     *
     * @param {Object} navItem A navigation item, either left menu element or contextual element
     *
     * @returns {void}
     *
     * @private
     */
    var updateNavItem = function updateNavItem (navItem) {

        var countItem = navItem.find('.events-count');
        updateCountItem(countItem, difference);
    };

    /**
     * Will update both menu items (sidebar left and contextual) for a given slug
     *
     * @param {String} sportSlug sport slug text
     *
     * @returns {void}
     *
     * @private
     */
    var updateMenuItemsForSlug = function updateMenuItemsForSlug (sportSlug) {

        var navItems = getNavItemsFromSlug(sportSlug);
        _.each(navItems, updateNavItem);
    };

    /**
     * In case we have a menu, we update the respective menu items for the given event sport slug
     *
     * @param {String} sportSlug sport slug text
     *
     * @returns {void}
     *
     * @private
     */
    var processAllSportsNavigation = function processAllSportsNavigation (sportSlug) {

        updateMenuItemsForSlug(sportSlug);

        //When event sport is updating, should also update All Sports
        updateMenuItemsForSlug('in-play');
    };

    if (hasAllSportsNavigation) {

        processAllSportsNavigation(sportSlug);
    }
};

/**
 * Function resets internals objects values
 *
 * @returns {void}
 *
 * @private
 */
var resetInternalsValues = function resetInternalsValues () {

    content = null;
    deps = null;
    args = null;
    now = null;
    sportNavigationItems = [];
    sportNavigationContainer = null;
    contextualMenu = null;
    contextualNavigationItems = [];
    hasAllSportsNavigation = null;

    BasePage.setObjectValues(pagination, {
        baseLink: null,
        pointers: [],
        currentPointers: null,
        request: null,
        rowIds: [],
    });
};

/**
 * set a flag that will indicate if the page contains a menu with all in play sports
 *
 * @param {Object} args Page arguments
 *
 * @returns {String} Compiled URL based on args
 *
 * @private
 */
var setAllSportsNavigation = function setAllSportsNavigation () {

    if (hasAllSportsNavigation === null) {

        var urlArgs = BasePage.UrlArgsProvider.get();
        hasAllSportsNavigation =
            !_.isEmpty(urlArgs) && (urlArgs.sport === 'in-play');
    }
};

/**
 * Return the URL, for a single sport, associated with this page based on the args
 *
 * @param {Object} args Page arguments
 *
 * @returns {String} Compiled URL based on args
 *
 * @private
 */
var getSingleSportContentUrl = function getSingleSportContentUrl (args) {

    setAllSportsNavigation();

    if (hasAllSportsNavigation) {

        var streamingFilterPath = args.streamed ? '/streamed' : '';
        return '/betting/' + args.locale + '/in-play/' + args.sport + streamingFilterPath;
    } else {

        return '/betting/' + args.locale + '/' + args.sport + '/in-play';
    }
};

/**
 * Redirect to the All Sports In-Play Page if there is no active In-Play events for the sport
 *
 * @returns {void}
 *
 * @private
 */
var goToInPlayAllIfNoEvents = function () {

    if (hasAllSportsNavigation) {

        var goToPathName = '/betting/' + args.locale + '/in-play/all';
        var goToPathNameTranslated = Translator.translateUrl(goToPathName, 'from');
        var currentPathName = window.location.pathname;
        var noInPlayEvents = window.WH.sportsbook.totalRowsCount === undefined ||
            window.WH.sportsbook.totalRowsCount === 0;

        if (currentPathName !== goToPathNameTranslated && noInPlayEvents) {

            BasePage.PageCommon.navigate(goToPathNameTranslated);
        }
    }
};

/**
 * Push related functionality
 *
 * @type {Object}
 */
var push = {

    /**
     * Array containing event IDs that were already removed by push. Since we are just handling display false
     * messages so far, we need to store them to avoid processing the messages twice.
     *
     * @type {Array}
     */
    removedEvents: [],

    /**
     * DOM PUSH update callback function
     * This function is called every time a push
     * message comes in
     *
     * @param {Object} message DOM PUSH message
     *
     * @private
     */
    callback: function (message) {

        if (push.handlers.hasOwnProperty(message.attribute)) {
            push[push.handlers[message.attribute]](message);
        }
    },

    /**
     * PUSH handlers available per attribute type
     *
     * @type {Object}
     */
    handlers: {
        'betInRun'  : 'betInRunUpdateCase',
        'displayed'  : 'displayedUpdateCase',
        'startDateTime'  : 'startDateTimeUpdateCase',
    },

    /**
     * Will handle the startDateTime push message for event
     *
     * @param {Object} message Update change message
     *
     * @return {void}
     *
     * @private
     */
    startDateTimeUpdateCase: (message) =>
        LiveScore.startDateTimeUpdateCase(message, content),

    /**
     * Will handle an event push message
     *
     * @param {Object} $event event object
     * @param {Object} message PUSH message
     *
     * @return {void}
     *
     * @private
     */
    handleEventUpdateCase: function ($event, message) {

        //if the event was already removed before we are not decreasing the count again
        if (!_.includes(push.removedEvents, message.id)) {

            handleEventsCount($event, !message.value, message.id);

            push.removedEvents.push(message.id);
        }
    },

    /**
     * Handles the dom push message containing an display attribute
     * The name of this function is linked to push.handlers
     *
     * @param  {Object} message PUSH message
     *
     * @return {void}
     *
     * @private
     */
    displayedUpdateCase: function (message) {

        if (message.nodeType === 'events' &&
            !message.value &&
            window.WH.sportsbook.model.events[message.id].isInPlay.toString() === 'true') {

            var $event = BasePage.jQuery('#' + message.id);

            push.handleEventUpdateCase($event, message);

            LiveScore.handleLivescore(message, content);
            manageEventContainersHeaders(message.id, $event, !message.value);
        }
    },

    /**
     * Handles the dom push message containing an betInRun attribute
     * The name of this function is linked to push.handlers
     *
     * @param  {Object} message PUSH message
     *
     * @return {void}
     *
     * @private
     */
    betInRunUpdateCase: function (message) {

        if(message.nodeType === 'events' &&
            !message.value &&
            window.WH.sportsbook.model.events[message.id].displayed.toString() === 'true') {

            var $event = BasePage.jQuery('#' + message.id);

            $event.toggleClass('displayNone', !message.value);

            push.handleEventUpdateCase($event, message);

            LiveScore.handleLivescore(message, content);
            manageEventContainersHeaders(message.id, $event, !message.value);
        }
    },

    /**
     * Removes event IDs that were already removed by push.
     *
     * @return {void}
     *
     * @private
     */
    clearRemovedEvents: function () {
        push.removedEvents = [];
    },
};

/**
 * Contains functions exposed by this component
 * as an API
 *
 * @type {Object}
 */
var api = {

    /**
     * Return the URL associated with this page based on the args
     *
     * @param {Object} args Page arguments
     *
     * @returns {String} Compiled URL based on args
     *
     * @public
     */
    getContentURL: function (args) {

        if (args.sport === 'in-play') {

            var streamingFilterPath = args.streamed ? '/streamed' : '';

            return '/betting/' + args.locale + '/in-play/all' + streamingFilterPath;
        } else {

            return getSingleSportContentUrl(args);
        }
    },

    /**
     * Function called after the DOM ready event is fired
     *
     * @param {Object} deps Object that contains page dependencies
     *
     * @returns {void}
     *
     * @public
     */
    postReady: function (deps, content, args) {

        BodyStateAdapter.init(BasePage.jQuery, 'inPlay', args.sport);

        if (deps.ready) {
            deps.ready('inPlay');
        }
    },

    /**
     * Function called after rendering the page
     *
     * @param {Object} deps Object that contains page dependencies
     * @param {Object} content jQuery object that contains this page
     *
     * @returns {void}
     *
     * @public
     */
    postRender: function (deps, content) {

        initPostRender();

        if (hasAllSportsNavigation) {

            cacheSportNavigationItems();
        }

        BasePage.switchTab('Inplay');

        pagination.init();

        goToInPlayAllIfNoEvents();
    },

    bindNavigation: bindNavigation,

    /**
     * Changes rendered state flag to true
     *
     * @public
     */
    setRendered: function () {

        now = BasePage.moment.utc();
        rendered = true;
    },

    /**
     * Responsible for unsetting, unbinding all related actions to this Page
     *
     * @returns {void}
     *
     * @public
     */
    setUnrendered: function () {

        if (rendered) {

            BasePage.LazyLoadingViewAdapter.destroy();
        }

        resetInternalsValues();
        rendered = false;
    },

    /**
     * Returns the value of the internal rendered state flag
     *
     * @returns {Boolean} Returns true or false
     *
     * @public
     */
    isRendered: function () {

        return rendered;
    },
};


export default api;
